import type { ImageSourcePropType } from 'react-native';

export type ArticleCollection = 'compare' | 'guides';

export type ArticleSource = {
  label: string;
  href: `https://${string}`;
};

export type ArticleSummary = {
  slug: string;
  path: `/${ArticleCollection}/${string}`;
  collection: ArticleCollection;
  pageType: 'compare' | 'guide';
  title: string;
  description: string;
  categoryLabel: string;
  publishedLabel: string;
  updatedIso: string;
  seoTitle: string;
  seoDescription: string;
  heroImage: ImageSourcePropType;
  heroImageAlt: string;
  heroImageFrame?: 'desktop' | 'phone';
  excerpt: string;
  sources?: ArticleSource[];
};

const dashboardImage = require('../../assets/screenshots/desktop-dashboard.png');
const dashboardDarkImage = require('../../assets/screenshots/desktop-dashboard-dark.png');
const firePlanImage = require('../../assets/screenshots/desktop-fire-plan.png');
const budgetImage = require('../../assets/screenshots/desktop-budget.png');
const balanceSheetImage = require('../../assets/screenshots/desktop-balance-sheet.png');
const transactionsImage = require('../../assets/screenshots/desktop-transactions.png');
const addPartnerImage = require('../../assets/screenshots/desktop-add-a-partner.png');

const mobileHome = require('../../assets/screenshots/mobile-home.png');
const mobileLedger = require('../../assets/screenshots/mobile-ledger.png');
const mobileAccounts = require('../../assets/screenshots/mobile-accounts.png');
const mobileFirePlan = require('../../assets/screenshots/mobile-fire-plan-redesign.png');
const mobileBudget = require('../../assets/screenshots/mobile-budget-redesign.png');
const mobileAskAi = require('../../assets/screenshots/mobile-ask-ai.png');
const mobileScenarios = require('../../assets/store/screenshot-sources/mobile-scenarios.png');
const onboardingConnect = require('../../assets/store/screenshot-sources/onboarding-connect.png');
const onboardingReady = require('../../assets/store/screenshot-sources/onboarding-ready.png');

export const ARTICLE_SUMMARIES: ArticleSummary[] = [
  {
    slug: 'empower-alternatives-fire',
    path: '/compare/empower-alternatives-fire',
    collection: 'compare',
    pageType: 'compare',
    title: 'Best Empower Alternatives for FIRE Households',
    description:
      'A practical guide to the best Empower alternatives if you care about both day-to-day spending and long-term financial independence planning.',
    categoryLabel: 'Comparison',
    publishedLabel: 'March 2026',
    updatedIso: '2026-03-24',
    seoTitle: 'Best Empower Alternatives for FIRE Households | Per Diem',
    seoDescription:
      'Looking for an Empower alternative for FIRE planning? Compare the best options for connected budgeting, net worth tracking, and retirement planning.',
    heroImage: mobileHome,
    heroImageAlt:
      'Per Diem mobile home screen showing daily spend guidance and financial planning context.',
    heroImageFrame: 'phone',
    excerpt:
      'The best Empower alternative depends on whether you want free net worth tracking, stronger day-to-day budgeting, or a tighter connection between current spending and your FIRE timeline.',
  },
  {
    slug: 'per-diem-vs-empower',
    path: '/compare/per-diem-vs-empower',
    collection: 'compare',
    pageType: 'compare',
    title: 'Per Diem vs Empower',
    description:
      'A direct comparison of Per Diem and Empower for households that want better visibility into both daily spending and long-term retirement outcomes.',
    categoryLabel: 'Comparison',
    publishedLabel: 'March 2026',
    updatedIso: '2026-03-24',
    seoTitle: 'Per Diem vs Empower: Which Is Better for FIRE Planning? | Per Diem',
    seoDescription:
      'Compare Per Diem vs Empower on budgeting, net worth tracking, account sync, and FIRE planning to decide which fits your household better.',
    heroImage: mobileFirePlan,
    heroImageAlt:
      'Per Diem mobile FIRE plan screen showing retirement planning context.',
    heroImageFrame: 'phone',
    excerpt:
      'Empower is strong if you want a free dashboard and retirement planner. Per Diem is stronger if you want daily spending, budgeting, and FIRE planning to live in one connected workflow.',
  },
  {
    slug: 'monarch-money-alternatives-fire',
    path: '/compare/monarch-money-alternatives-fire',
    collection: 'compare',
    pageType: 'compare',
    title: 'Best Monarch Money Alternatives for People Pursuing FIRE',
    description:
      'The best alternatives to Monarch Money if you want stronger financial independence planning, live account context, and household-level decision making.',
    categoryLabel: 'Comparison',
    publishedLabel: 'March 2026',
    updatedIso: '2026-03-24',
    seoTitle: 'Best Monarch Money Alternatives for People Pursuing FIRE | Per Diem',
    seoDescription:
      'Looking for a Monarch Money alternative built for FIRE households? Compare the best options for budgeting, planning, and account-connected retirement tracking.',
    heroImage: mobileBudget,
    heroImageAlt:
      'Per Diem mobile budget screen showing category planning and daily spending context.',
    heroImageFrame: 'phone',
    excerpt:
      'Monarch Money is a strong budgeting product. The best alternatives depend on whether you need deeper FIRE planning, spreadsheet flexibility, or tighter day-to-day guidance.',
  },
  {
    slug: 'projectionlab-alternatives-fire',
    path: '/compare/projectionlab-alternatives-fire',
    collection: 'compare',
    pageType: 'compare',
    title: 'Best ProjectionLab Alternatives for FIRE Households',
    description:
      'A practical comparison for FIRE households that like deep retirement modeling but want budgeting, account context, or daily spending decisions closer to the plan.',
    categoryLabel: 'Comparison',
    publishedLabel: 'June 2026',
    updatedIso: '2026-06-01',
    seoTitle: 'Best ProjectionLab Alternatives for FIRE Households | Per Diem',
    seoDescription:
      'Looking for a ProjectionLab alternative? Compare FIRE planning tools for households that want budgeting, account sync, scenario planning, and daily spending guidance.',
    heroImage: mobileScenarios,
    heroImageAlt:
      'Per Diem mobile scenario planning screen showing saved FIRE what-if plans.',
    heroImageFrame: 'phone',
    excerpt:
      'ProjectionLab is excellent for deep planning. The right alternative depends on whether you want the FIRE model to connect more directly to daily spending, live accounts, and household budgeting.',
  },
  {
    slug: 'best-fire-planning-apps',
    path: '/guides/best-fire-planning-apps',
    collection: 'guides',
    pageType: 'guide',
    title: 'Best FIRE Planning Apps for Daily Decisions',
    description:
      'A practical guide to choosing a FIRE planning app when you want more than a retirement calculator: budgeting, account context, scenarios, and daily spending guidance.',
    categoryLabel: 'Guide',
    publishedLabel: 'June 2026',
    updatedIso: '2026-06-01',
    seoTitle: 'Best FIRE Planning Apps for Daily Decisions | Per Diem',
    seoDescription:
      'Compare FIRE planning apps by job: retirement projections, daily spending decisions, account tracking, scenario planning, and household budgeting.',
    heroImage: mobileFirePlan,
    heroImageAlt:
      'Per Diem mobile FIRE plan screen showing financial independence planning context.',
    heroImageFrame: 'phone',
    excerpt:
      'The best FIRE planning app depends on whether your biggest gap is a better calculator, better tracking, or a way to connect today’s spending to the long-term plan.',
  },
  {
    slug: 'how-much-can-i-spend-today',
    path: '/guides/how-much-can-i-spend-today',
    collection: 'guides',
    pageType: 'guide',
    title: 'How Much Can I Spend Today?',
    description:
      'A daily spending framework for households that want a clear safe-to-spend number without losing sight of savings, bills, and the long-term FIRE plan.',
    categoryLabel: 'Guide',
    publishedLabel: 'June 2026',
    updatedIso: '2026-06-01',
    seoTitle: 'How Much Can I Spend Today? A FIRE-Aware Daily Spending Guide | Per Diem',
    seoDescription:
      'Learn how to calculate a safe daily spending number from income, fixed bills, savings goals, recent spending, and your long-term financial independence plan.',
    heroImage: mobileHome,
    heroImageAlt:
      'Per Diem mobile home screen showing a daily spending number connected to household finances.',
    heroImageFrame: 'phone',
    excerpt:
      'A useful daily spending number is not just your checking balance divided by the days left this month. It needs to account for bills, goals, recent spending, and the plan you are trying not to drift away from.',
  },
  {
    slug: 'from-sign-up-to-a-real-fire-plan',
    path: '/guides/from-sign-up-to-a-real-fire-plan',
    collection: 'guides',
    pageType: 'guide',
    title: 'From Sign Up to a Real FIRE Plan',
    description:
      'A walkthrough of the Per Diem onboarding flow and why it is designed to get a household from account creation to an actionable plan without making setup feel like a second job.',
    categoryLabel: 'Guide',
    publishedLabel: 'April 2026',
    updatedIso: '2026-04-19',
    seoTitle: 'From Sign Up to a Real FIRE Plan | Per Diem',
    seoDescription:
      'See how Per Diem takes a new household from sign up to a usable FIRE plan with a lightweight onboarding flow, optional account linking, and practical defaults.',
    heroImage: onboardingReady,
    heroImageAlt:
      'Per Diem onboarding completion screen showing a FIRE plan ready state.',
    heroImageFrame: 'phone',
    excerpt:
      'Per Diem onboarding is meant to feel like a fast planning setup, not a giant financial homework assignment. The goal is to get you to a usable FIRE plan quickly, then let the model get richer over time.',
  },
  {
    slug: 'your-daily-freedom-number',
    path: '/guides/your-daily-freedom-number',
    collection: 'guides',
    pageType: 'guide',
    title: 'Your Daily Freedom Number',
    description:
      'Why Per Diem centers the product on a daily spending number and how that one metric connects budgets, balances, and your FIRE timeline into something you can actually use.',
    categoryLabel: 'Guide',
    publishedLabel: 'April 2026',
    updatedIso: '2026-04-19',
    seoTitle: 'Your Daily Freedom Number: How Per Diem Makes FIRE Actionable | Per Diem',
    seoDescription:
      'Learn how Per Diem translates connected budgets, account data, and FIRE projections into a daily spending number that households can actually act on.',
    heroImage: mobileHome,
    heroImageAlt:
      'Per Diem mobile home screen showing the daily spending number and connected financial context.',
    heroImageFrame: 'phone',
    excerpt:
      'Many finance tools show you what happened. Per Diem is trying to answer a harder question: what can you spend today without quietly changing your long-term plan?',
  },
  {
    slug: 'connect-everything-even-the-weird-stuff',
    path: '/guides/connect-everything-even-the-weird-stuff',
    collection: 'guides',
    pageType: 'guide',
    title: 'Connect Everything, Even the Weird Stuff',
    description:
      'A tour of the account layer in Per Diem, from connected banking and brokerage sync to manual assets, liabilities, mortgages, and family context that rarely fit cleanly into other tools.',
    categoryLabel: 'Guide',
    publishedLabel: 'April 2026',
    updatedIso: '2026-04-19',
    seoTitle: 'Connect Everything, Even the Weird Stuff | Per Diem',
    seoDescription:
      'See how Per Diem handles connected accounts, manual balances, mortgages, vehicles, kids, and other real-life household data that most planning stacks split across separate tools.',
    heroImage: mobileAccounts,
    heroImageAlt:
      'Per Diem mobile accounts screen showing household assets, liabilities, and connected account context.',
    heroImageFrame: 'phone',
    excerpt:
      'Real households have mortgages, 529s, stock plans, vehicles, manual accounts, and half-finished spreadsheets. Per Diem is built to model that mess without asking you to pretend it does not exist.',
  },
  {
    slug: 'a-balance-sheet-built-for-real-life',
    path: '/guides/a-balance-sheet-built-for-real-life',
    collection: 'guides',
    pageType: 'guide',
    title: 'A Balance Sheet Built for Real Life',
    description:
      'Why a useful household balance sheet needs to handle more than neat checking and brokerage accounts, and how Per Diem models the assets and liabilities that actually shape financial independence.',
    categoryLabel: 'Guide',
    publishedLabel: 'April 2026',
    updatedIso: '2026-04-19',
    seoTitle: 'A Balance Sheet Built for Real Life | Per Diem',
    seoDescription:
      'See how Per Diem handles mortgages, vehicles, manual assets, cash, investments, and other real-world household balance sheet details in one planning model.',
    heroImage: balanceSheetImage,
    heroImageAlt:
      'Per Diem balance sheet screen with household assets and liabilities.',
    excerpt:
      'A useful balance sheet should reflect how a household actually lives, not just the cleanest slice of what sync providers can pull in automatically.',
  },
  {
    slug: 'budgeting-from-take-home-pay-not-gross-fantasy',
    path: '/guides/budgeting-from-take-home-pay-not-gross-fantasy',
    collection: 'guides',
    pageType: 'guide',
    title: 'Budgeting From Take-Home Pay, Not Gross Fantasy',
    description:
      'A guide to why FIRE-minded budgeting gets distorted when payroll deductions and real take-home pay are ignored, and how Per Diem treats budgeting as part of the full household model.',
    categoryLabel: 'Guide',
    publishedLabel: 'April 2026',
    updatedIso: '2026-04-19',
    seoTitle: 'Budgeting From Take-Home Pay, Not Gross Fantasy | Per Diem',
    seoDescription:
      'Learn why Per Diem budgets from take-home pay and payroll-aware income instead of pretending gross salary is the money a household can actually spend.',
    heroImage: mobileBudget,
    heroImageAlt:
      'Per Diem mobile budget screen showing category planning for a household budget.',
    heroImageFrame: 'phone',
    excerpt:
      'Gross salary is not a spending plan. A useful budget starts from what actually lands in the household and what is already committed before discretionary spending begins.',
  },
  {
    slug: 'tax-aware-fire-planning',
    path: '/guides/tax-aware-fire-planning',
    collection: 'guides',
    pageType: 'guide',
    title: 'Tax-Aware FIRE Planning',
    description:
      'Why long-term planning gets more realistic when taxes, deductions, and account treatment are part of the model instead of an afterthought layered on in a spreadsheet.',
    categoryLabel: 'Guide',
    publishedLabel: 'April 2026',
    updatedIso: '2026-04-19',
    seoTitle: 'Tax-Aware FIRE Planning | Per Diem',
    seoDescription:
      'See how Per Diem brings tax context into FIRE planning with account treatment, deductions, and planning assumptions that shape what your plan actually means.',
    heroImage: mobileAccounts,
    heroImageAlt:
      'Per Diem mobile accounts screen used alongside tax-aware financial planning context.',
    heroImageFrame: 'phone',
    excerpt:
      'A FIRE plan that ignores taxes can still be directionally useful, but it is rarely the plan a household can actually live on. Per Diem tries to close that gap.',
  },
  {
    slug: 'aca-health-insurance-early-retirement',
    path: '/guides/aca-health-insurance-early-retirement',
    collection: 'guides',
    pageType: 'guide',
    title: 'ACA Health Insurance for Early Retirement',
    description:
      'A detailed FIRE planning guide to ACA Marketplace coverage, premium tax credits, spouses, children, income thresholds, COBRA, Medicare timing, and tax-year reconciliation.',
    categoryLabel: 'Guide',
    publishedLabel: 'August 2026',
    updatedIso: '2026-08-23',
    seoTitle: 'ACA Health Insurance for Early Retirees | FIRE Planning Guide | Per Diem',
    seoDescription:
      'Plan ACA health insurance before Medicare with a FIRE-focused guide to Marketplace subsidies, MAGI, spouse and dependent coverage, COBRA, Medicare timing, and tax implications.',
    heroImage: mobileScenarios,
    heroImageAlt:
      'Per Diem mobile scenario planning screen used for early retirement health insurance planning.',
    heroImageFrame: 'phone',
    excerpt:
      'ACA health insurance can be one of the largest planning variables before Medicare. A serious FIRE plan should model household MAGI, subsidies, spouses, kids, COBRA, Medicare timing, and tax reconciliation risk.',
    sources: [
      {
        label: 'HealthCare.gov: What to include as income for Marketplace coverage',
        href: 'https://www.healthcare.gov/income-and-household-information/income/',
      },
      {
        label: 'HealthCare.gov: Health coverage for retirees',
        href: 'https://www.healthcare.gov/retirees/',
      },
      {
        label: 'HHS: 2025 poverty guidelines computations',
        href: 'https://aspe.hhs.gov/topics/poverty-economic-mobility/poverty-guidelines/prior-hhs-poverty-guidelines-federal-register-references/2025-poverty-guidelines-computations',
      },
      {
        label: 'IRS: Premium Tax Credit frequently asked questions',
        href: 'https://www.irs.gov/newsroom/irs-updates-frequently-asked-questions-on-the-premium-tax-credit',
      },
      {
        label: 'IRS: About Form 8962, Premium Tax Credit',
        href: 'https://www.irs.gov/forms-pubs/about-form-8962',
      },
      {
        label: 'Social Security Administration: Tax data used for Medicare IRMAA',
        href: 'https://www.ssa.gov/OP_Home/handbook/handbook.25/handbook-2504.html',
      },
    ],
  },
  {
    slug: 'planning-for-kids-college-and-family-life-events',
    path: '/guides/planning-for-kids-college-and-family-life-events',
    collection: 'guides',
    pageType: 'guide',
    title: 'Planning for Kids, College, and Family Life Events',
    description:
      'How Per Diem handles children, education goals, and recurring family events so the household plan can reflect the commitments that change what financial freedom looks like.',
    categoryLabel: 'Guide',
    publishedLabel: 'April 2026',
    updatedIso: '2026-04-19',
    seoTitle: 'Planning for Kids, College, and Family Life Events | Per Diem',
    seoDescription:
      'Learn how Per Diem models dependents, education goals, and recurring family life events inside a connected household FIRE plan.',
    heroImage: addPartnerImage,
    heroImageAlt:
      'Per Diem family and household planning context.',
    excerpt:
      'Children, college plans, and future household events are not side notes. They are central inputs into the kind of financial freedom a family can realistically pursue.',
  },
  {
    slug: 'what-if-i-retire-earlier',
    path: '/guides/what-if-i-retire-earlier',
    collection: 'guides',
    pageType: 'guide',
    title: 'What If I Retire Earlier?',
    description:
      'A guide to scenarios, tradeoffs, and year-by-year planning in Per Diem for households that want to test an earlier FI date instead of treating retirement as one fixed target.',
    categoryLabel: 'Guide',
    publishedLabel: 'April 2026',
    updatedIso: '2026-04-19',
    seoTitle: 'What If I Retire Earlier? | Per Diem',
    seoDescription:
      'Explore how Per Diem helps households test earlier retirement scenarios, contribution changes, and tradeoffs inside a connected FIRE plan.',
    heroImage: mobileScenarios,
    heroImageAlt:
      'Per Diem mobile scenario planning screen for earlier retirement analysis.',
    heroImageFrame: 'phone',
    excerpt:
      'The interesting planning question is often not “When do I retire?” but “What would have to change if I wanted to retire sooner?”',
  },
  {
    slug: 'how-we-turn-transaction-cleanup-into-better-planning',
    path: '/guides/how-we-turn-transaction-cleanup-into-better-planning',
    collection: 'guides',
    pageType: 'guide',
    title: 'How We Turn Transaction Cleanup Into Better Planning',
    description:
      'Why reviewing and categorizing transactions matters more when it feeds a connected household model, and how Per Diem turns cleanup work into planning leverage.',
    categoryLabel: 'Guide',
    publishedLabel: 'April 2026',
    updatedIso: '2026-04-19',
    seoTitle: 'How We Turn Transaction Cleanup Into Better Planning | Per Diem',
    seoDescription:
      'See how Per Diem uses transaction review and categorization to improve budgeting, daily spend guidance, and long-term FIRE planning.',
    heroImage: mobileLedger,
    heroImageAlt:
      'Per Diem mobile transaction ledger with categorized spending history.',
    heroImageFrame: 'phone',
    excerpt:
      'Transaction cleanup is tedious when it goes nowhere. It becomes more valuable when every corrected category makes the household plan more useful.',
  },
  {
    slug: 'ask-your-financial-questions-in-plain-english',
    path: '/guides/ask-your-financial-questions-in-plain-english',
    collection: 'guides',
    pageType: 'guide',
    title: 'Ask Your Financial Questions in Plain English',
    description:
      'A guide to the Per Diem AI planning assistant and why conversational access to your household model matters more when it is grounded in connected balances, budgets, and projections.',
    categoryLabel: 'Guide',
    publishedLabel: 'April 2026',
    updatedIso: '2026-04-19',
    seoTitle: 'Ask Your Financial Questions in Plain English | Per Diem',
    seoDescription:
      'Learn how the Per Diem AI planning assistant helps households ask plain-English questions about connected financial data, budgets, and FIRE plans.',
    heroImage: mobileAskAi,
    heroImageAlt:
      'Per Diem mobile Ask AI screen with plain-English financial prompts.',
    heroImageFrame: 'phone',
    excerpt:
      'The value of an AI planning assistant is not that it sounds smart. It is that it can answer questions inside the real context of your household instead of from generic financial trivia.',
  },
  {
    slug: 'build-a-shared-plan-with-your-partner',
    path: '/guides/build-a-shared-plan-with-your-partner',
    collection: 'guides',
    pageType: 'guide',
    title: 'Build a Shared Plan With Your Partner',
    description:
      'Why household planning works better when both people can see the same numbers, assumptions, and tradeoffs instead of managing separate mental models.',
    categoryLabel: 'Guide',
    publishedLabel: 'April 2026',
    updatedIso: '2026-04-19',
    seoTitle: 'Build a Shared Plan With Your Partner | Per Diem',
    seoDescription:
      'See how Per Diem supports household collaboration so couples can make financial decisions from the same plan instead of separate spreadsheets and assumptions.',
    heroImage: addPartnerImage,
    heroImageAlt:
      'Per Diem household collaboration view for adding a partner.',
    excerpt:
      'A household plan becomes more useful when it stops living in one person’s spreadsheet and becomes something both partners can see, discuss, and refine together.',
  },
  {
    slug: 'exports-notifications-and-weekly-accountability',
    path: '/guides/exports-notifications-and-weekly-accountability',
    collection: 'guides',
    pageType: 'guide',
    title: 'Exports, Notifications, and Weekly Accountability',
    description:
      'How Per Diem extends beyond the main dashboard with exports, reminders, and repeatable check-ins that help a household keep the plan alive after the initial setup.',
    categoryLabel: 'Guide',
    publishedLabel: 'April 2026',
    updatedIso: '2026-04-19',
    seoTitle: 'Exports, Notifications, and Weekly Accountability | Per Diem',
    seoDescription:
      'Learn how exports, notifications, and recurring check-ins help Per Diem households stay engaged with their plan after onboarding.',
    heroImage: mobileHome,
    heroImageAlt:
      'Per Diem mobile home screen representing household financial check-ins and accountability.',
    heroImageFrame: 'phone',
    excerpt:
      'The most useful plan is one a household revisits. Exports, reminders, and lightweight accountability features help the product stay active between major decisions.',
  },
];

export const ARTICLE_SUMMARY_BY_SLUG = Object.fromEntries(
  ARTICLE_SUMMARIES.map((article) => [article.slug, article]),
) as Record<string, ArticleSummary>;

export const COMPARE_ARTICLES = ARTICLE_SUMMARIES.filter(
  (article) => article.collection === 'compare',
);

export const GUIDE_ARTICLES = ARTICLE_SUMMARIES.filter(
  (article) => article.collection === 'guides',
);

export const ARTICLE_IMAGES = {
  dashboard: dashboardImage,
  dashboardDark: dashboardDarkImage,
  firePlan: firePlanImage,
  budget: budgetImage,
  balanceSheet: balanceSheetImage,
  transactions: transactionsImage,
  addPartner: addPartnerImage,
  mobileHome,
  mobileLedger,
  mobileAccounts,
  mobileFirePlan,
  mobileBudget,
  mobileAskAi,
  mobileScenarios,
  onboardingConnect,
  onboardingReady,
} as const;

export type CarouselSlide = {
  key: string;
  image: ImageSourcePropType;
  label: string;
  description: string;
};

export const HERO_CAROUSEL_SLIDES: CarouselSlide[] = [
  {
    key: 'home',
    image: mobileHome,
    label: 'Your per diem',
    description: 'One number for what you can spend today — connected to your net worth, FI age, and portfolio.',
  },
  {
    key: 'ledger',
    image: mobileLedger,
    label: 'Ledger',
    description: 'Every purchase categorized and tracked against your budget automatically.',
  },
  {
    key: 'accounts',
    image: mobileAccounts,
    label: 'Accounts',
    description: 'Cash, investments, debt, and tracked assets in one balance sheet.',
  },
  {
    key: 'fire-plan',
    image: mobileFirePlan,
    label: 'FIRE plan',
    description: 'Year-by-year projections with Monte Carlo simulations on your real numbers.',
  },
  {
    key: 'budget',
    image: mobileBudget,
    label: 'Budget',
    description: 'Discretionary spending targets that feed directly into your per diem and FIRE plan.',
  },
  {
    key: 'ask-ai',
    image: mobileAskAi,
    label: 'Ask AI',
    description: 'Plain-English answers grounded in your household spending, savings, and FIRE timeline.',
  },
];
